import { useId } from 'react';
import { Settings2 } from 'lucide-react';
import { ENGINE_LABEL } from '../engine/config';
import {
  DEFAULT_ENGINE_SETTINGS,
  MAX_DEPTH,
  MAX_LINES,
  MIN_DEPTH,
  MIN_LINES,
  hashOptionsForDevice,
  resetEngineSettings,
  useEngineSettings,
  writeEngineSettings,
} from '../engine/settings';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from './ui/dialog';
import './engine-settings.css';

/**
 * Lichess-style tuning for the browser engine. Changes apply immediately (the next
 * search uses them) and stay in this browser: they describe this device, not the
 * account (see `engine/settings.ts`).
 */
export function EngineSettingsButton(): JSX.Element {
  return (
    <Dialog>
      <DialogTrigger
        className="engine-settings-trigger"
        aria-label="Engine settings"
        title="Engine settings"
      >
        <Settings2 size={16} aria-hidden="true" />
      </DialogTrigger>
      <DialogContent className="engine-settings">
        <DialogTitle>Engine settings</DialogTitle>
        <DialogDescription>
          {ENGINE_LABEL} runs on this device. These settings are saved in this browser only.
        </DialogDescription>
        <EngineSettingsForm />
      </DialogContent>
    </Dialog>
  );
}

export function EngineSettingsForm(): JSX.Element {
  const settings = useEngineSettings();
  const id = useId();
  const hashOptions = hashOptionsForDevice();
  // A size saved on a bigger device stays selectable rather than silently vanishing.
  if (!hashOptions.includes(settings.hashMb)) hashOptions.push(settings.hashMb);

  return (
    <div className="engine-settings-form">
      <div className="engine-setting">
        <label htmlFor={`${id}-lines`}>Lines</label>
        <input
          id={`${id}-lines`}
          type="range"
          min={MIN_LINES}
          max={MAX_LINES}
          step={1}
          value={settings.lines}
          onChange={(event) => writeEngineSettings({ lines: Number(event.target.value) })}
        />
        <output htmlFor={`${id}-lines`}>{settings.lines}</output>
        <p className="engine-setting-hint">Best moves to show in the engine readout.</p>
      </div>

      <div className="engine-setting">
        <label htmlFor={`${id}-depth`}>Depth</label>
        <input
          id={`${id}-depth`}
          type="range"
          min={MIN_DEPTH}
          max={MAX_DEPTH}
          step={1}
          value={settings.depth}
          onChange={(event) => writeEngineSettings({ depth: Number(event.target.value) })}
        />
        <output htmlFor={`${id}-depth`}>{settings.depth}</output>
        <p className="engine-setting-hint">
          Deeper is stronger but slower. Every extra ply takes noticeably longer on a
          single thread.
        </p>
      </div>

      <div className="engine-setting">
        <label htmlFor={`${id}-hash`}>Memory</label>
        <select
          id={`${id}-hash`}
          value={settings.hashMb}
          onChange={(event) => writeEngineSettings({ hashMb: Number(event.target.value) })}
        >
          {hashOptions.map((size) => (
            <option key={size} value={size}>
              {size} MB
            </option>
          ))}
        </select>
        <p className="engine-setting-hint">
          Hash table size. More memory helps deep searches. Changing it clears what the
          engine has learned so far.
        </p>
      </div>

      <div className="engine-setting">
        <span className="engine-setting-label">Threads</span>
        <span className="engine-setting-static">1</span>
        <p className="engine-setting-hint">
          This build is single-threaded, so it runs in any browser without special
          server headers.
        </p>
      </div>

      <button
        type="button"
        className="engine-settings-reset"
        onClick={() => resetEngineSettings()}
        disabled={
          settings.lines === DEFAULT_ENGINE_SETTINGS.lines &&
          settings.depth === DEFAULT_ENGINE_SETTINGS.depth &&
          settings.hashMb === DEFAULT_ENGINE_SETTINGS.hashMb
        }
      >
        Reset to defaults
      </button>
    </div>
  );
}
